import { RefObject, useEffect, useState } from 'react';

interface DefectProgress {
  currentStageIndex: number;
  progressPercent: number;
}

const DESKTOP_PROGRESS_QUERY = '(min-width: 1024px)';
const STICKY_TOP = 120;

export default function useDefectProgress(
  sectionRef: RefObject<HTMLElement | null>,
) {
  const [progressState, setProgressState] = useState<DefectProgress>({
    currentStageIndex: 0,
    progressPercent: 0,
  });

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof window === 'undefined') {
      return undefined;
    }

    let frameId = 0;
    let isListening = false;

    const getMarks = () =>
      Array.from(section.querySelectorAll<HTMLElement>('[data-scale-mark]'));

    const setSectionProgress = (activeStage: number, progress: number) => {
      const progressPercent = Math.round(progress * 1000) / 10;
      section.style.setProperty(
        '--defect-progress-percent',
        `${progressPercent}%`,
      );
      section.style.setProperty(
        '--defect-glow-opacity',
        String(Math.round(progress * 1000) / 1000),
      );
      section.style.setProperty(
        '--defect-shadow-alpha',
        String(Math.round(progress * 0.72 * 1000) / 1000),
      );
      section.style.setProperty(
        '--defect-track-shadow-alpha',
        String(Math.round(progress * 0.18 * 1000) / 1000),
      );

      getMarks().forEach((mark, index) => {
        mark.toggleAttribute('data-active', index <= activeStage);
      });

      setProgressState((previous) =>
        previous.currentStageIndex === activeStage &&
        previous.progressPercent === progressPercent
          ? previous
          : { currentStageIndex: activeStage, progressPercent },
      );
    };

    const resetDefectProgress = () => {
      setSectionProgress(0, 0);
    };

    const updateDefectProgress = () => {
      frameId = 0;

      const marks = getMarks();
      const stageCount = marks.length;

      if (stageCount < 2) {
        return;
      }

      const stickyHeader = section.querySelector<HTMLElement>(
        '[data-sample-sticky-header]',
      );
      const panelHeight = stickyHeader
        ? stickyHeader.getBoundingClientRect().height
        : 0;

      const styles = window.getComputedStyle(section);
      const paddingTop = parseFloat(styles.paddingTop) || 0;
      const paddingBottom = parseFloat(styles.paddingBottom) || 0;
      const contentHeight = section.offsetHeight - paddingTop - paddingBottom;
      const contentTop = section.getBoundingClientRect().top + paddingTop;
      const scrollRange = Math.max(1, contentHeight - panelHeight);

      const progress = Math.min(
        1,
        Math.max(0, (STICKY_TOP - contentTop) / scrollRange),
      );
      const activeStage = Math.min(
        stageCount - 1,
        Math.floor(progress * (stageCount - 1) + 0.04),
      );

      setSectionProgress(activeStage, progress);
    };

    const requestUpdate = () => {
      if (frameId) {
        return;
      }
      frameId = window.requestAnimationFrame(updateDefectProgress);
    };

    const addListeners = () => {
      if (isListening) {
        return;
      }

      isListening = true;
      updateDefectProgress();
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
    };

    const removeListeners = () => {
      if (!isListening) {
        return;
      }

      isListening = false;
      if (frameId) {
        window.cancelAnimationFrame(frameId);
        frameId = 0;
      }
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };

    const mediaQuery = window.matchMedia(DESKTOP_PROGRESS_QUERY);
    const syncMode = () => {
      if (mediaQuery.matches) {
        addListeners();
        return;
      }

      removeListeners();
      resetDefectProgress();
    };

    syncMode();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncMode);
    } else {
      mediaQuery.addListener(syncMode);
    }

    return () => {
      removeListeners();
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', syncMode);
      } else {
        mediaQuery.removeListener(syncMode);
      }
    };
  }, [sectionRef]);

  return progressState;
}
